<template>
  <section id="product-family-details" :qaid="`${componentPrefix}-component`">
    <div class="flex items-center product-label-wrapper" aria-hidden="true">
      <product-label-group :product="product" class="mr-1 label-container" />
      <span
        v-if="product.marketingModelYear"
        qaid="product-marketing-model-year"
        class="text-md bg-gray-10 product-label"
      >
        {{ product.marketingModelYear }}
      </span>
      <span
        v-else-if="product.modelYearIntroAndCurrent && product.isArchived"
        qaid="product-model-year"
        class="text-md bg-gray-10 product-label"
      >
        {{ product.modelYearIntroAndCurrent }}
      </span>
    </div>
    <div class="p-2 md:mx-0 mb-2" :class="isElectraBrand ? 'shadow bg-white' : 'bg-gray-05'">
      <div>
        <pdl-heading :qaid="`${componentPrefix}-heading`" class="model-name" size="sm" :level="3">
          {{ product.name }}
        </pdl-heading>
        <p :qaid="`${componentPrefix}-pricing`" class="font-medium text-md">
          <product-price
            :variant-code="variantCode"
            :is-archived="product.isArchived"
            :is-updatable-by-customization="true"
            :has-30-day-price-display="true"
            :qaid="`${componentPrefix}-price`"
            class="pt-0"
          />
        </p>
        <p v-if="isRetailerPriceNoteEnabled" qaid="retailer-prices-vary-bp" class="text-xs italic">
          {{ $t('text.buyingZone.retailerPricesVary') }}
        </p>
        <p :qaid="`${componentPrefix}-sku-number`" class="text-gray-70 font-bold text-xs mt-25">
          {{ $t('product.details.sku', [productCode]) }}
        </p>
      </div>
      <product-reviews-header v-if="!kioskModeEnabledOnSite && product.powerReviewsID" />
      <product-reviews-summary
        v-if="product.powerReviewsID"
        class="my-2"
        :product-power-reviews-id="product.powerReviewsID"
        :qaid="`${componentPrefix}-reviews-summary`"
      />
      <p v-if="hasPositioningStatement" class="my-2">
        {{ product.copyPositioningStatement }}
      </p>
      <spec-details
        v-else
        type="secondary"
        :product="product"
        class="my-2"
        :is-showing-all-specs="false"
        qaid-label="buyzone-spec-details"
      />
      <div class="flex justify-between">
        <pdl-link
          :qaid="`${componentPrefix}-learn-more`"
          block
          medium
          standalone
          icon-right
          icon="chevron_right"
          @click="scrollToTabs"
        >
          {{ $t('text.learn.more') }}
        </pdl-link>
      </div>
    </div>
  </section>
</template>

<script>
import {mapMutations, mapState} from 'vuex';
import {PdlHeading} from '@pedal/pdl-section-header';
import {PdlLink} from '@pedal/pdl-link';
import {PdlToastType} from '@/constants/pdl-toast-type.js';
import SpecDetails from '@/familyDetailsPage/components/SpecDetails.vue';
import ProductReviewsSummary from '@/components/power-reviews/ProductReviewsSummary.vue';
import ProductReviewsHeader from '@/components/power-reviews/ProductReviewsHeader.vue';
import ProductPrice from '@/components/containers/buy-zone/ProductPrice.vue';
import {delayedScrollToElement} from '@/utils/scroll-to.js';
import ProductLabelGroup from '@/components/ProductLabelGroup.vue';
import {useFunctionalityToggle} from '@/composables/functionality-toggle.js';
import {FunctionalityToggle} from '@/constants/functionality-toggle.js';

export default {
  components: {
    PdlHeading,
    PdlLink,
    ProductReviewsSummary,
    ProductReviewsHeader,
    ProductPrice,
    ProductLabelGroup,
    SpecDetails,
  },
  props: {
    familyDetailsData: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    product: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    variantCode: {
      type: String,
      default: '',
      required: true,
    },
  },
  setup() {
    const {isToggleActive: isRetailerPriceNoteEnabled} = useFunctionalityToggle(
      FunctionalityToggle.RETAILER_PRICE_NOTE_TOGGLE
    );
    return {isRetailerPriceNoteEnabled};
  },
  data() {
    return {
      componentPrefix: 'product-family-details',
    };
  },
  computed: {
    ...mapState('backend', ['isElectraBrand', 'kioskModeEnabledOnSite']),
    ...mapState('productDetails', ['variantCode', 'tabHeaderHeight', 'isCustomizationMode']),
    ...mapState('familyDetails', ['selectedModelId']),
    ...mapState('global', ['headerHeight', 'isCompactView']),
    ...mapState('bikeCustomization', ['bikeData', 'incompatibleSelectionErrorToast']),

    hasPositioningStatement() {
      return (this.isElectraBrand || this.product?.productType !== 'bike') && this.product?.copyPositioningStatement;
    },
    productCode() {
      return this.variantCode || this.selectedModelId;
    },
  },
  watch: {
    incompatibleSelectionErrorToast(newValue) {
      if (newValue) {
        this.$notify({
          type: PdlToastType.ERROR,
          duration: 0,
          showClose: true,
          title: this.$t('projectOne.config.incompatible.options'),
          message: this.$t('projectOne.config.incompatible.conflicts'),
          action: newValue,
        });
        this.setIncompatibleSelectionErrorToast('');
      }
    },
  },
  methods: {
    ...mapMutations('productDetails', ['setActiveTab']),
    ...mapMutations('bikeCustomization', ['setIncompatibleSelectionErrorToast']),
    scrollToTabs() {
      const bikeOverviewElement = document.querySelector('.pdl-tabs__header');
      delayedScrollToElement(bikeOverviewElement, this.tabHeaderHeight, 50);
    },
  },
};
</script>

<style lang="scss" scoped>
.product-label-wrapper:has(.product-label),
.product-label-wrapper:has(.pdl-label) {
  margin-bottom: -0.75rem;

  + div {
    padding-top: 1.5rem;
  }
}

.model-name {
  line-height: 1.5;
}
</style>
